import type { KioskConfigDTO } from '@print-saas/shared';
import type { ConnectionState } from '../hooks/useKioskSocket';
import { useNow } from '../hooks/useNow';

const statusLabel: Record<ConnectionState, string> = {
  connecting: 'Connecting',
  connected: 'Online',
  reconnecting: 'Offline',
  'auth-error': 'Not paired',
};

const dotClass: Record<ConnectionState, string> = {
  connecting: 'bg-accent animate-pulse-dot',
  connected: 'bg-go',
  reconnecting: 'bg-bad animate-pulse-dot',
  'auth-error': 'bg-bad',
};

const timeFmt = new Intl.DateTimeFormat('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
const dateFmt = new Intl.DateTimeFormat('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });

type Printer = KioskConfigDTO['printer'];

function printerStatus(p: Printer): { label: string; dot: string } {
  if (!p.agentOnline) return { label: 'Printer offline', dot: 'bg-bad' };
  switch (p.state) {
    case 'IDLE':
      return { label: 'Printer ready', dot: 'bg-go' };
    case 'PRINTING':
      return { label: 'Printing', dot: 'bg-accent animate-pulse-dot' };
    case 'ERROR':
      return { label: p.message ? `Printer error: ${p.message}` : 'Printer error', dot: 'bg-bad animate-pulse-dot' };
    case 'OFFLINE':
      return { label: 'Printer offline', dot: 'bg-bad' };
    default:
      return { label: 'Printer status unknown', dot: 'bg-warn' };
  }
}

export function Footer({
  kioskCode,
  connection,
  printer,
  screenOnline,
}: {
  kioskCode: string | null;
  connection: ConnectionState;
  printer: Printer | null;
  /** Owner preview only: whether the real kiosk screen is connected (the preview's own connection says nothing about it). */
  screenOnline: boolean | null;
}) {
  const now = useNow(1000);
  const screen = screenOnline !== null && connection === 'connected';
  return (
    // Three columns so the credit stays centred whatever the status on the left says.
    <footer className="grid shrink-0 grid-cols-[1fr_auto_1fr] items-center gap-6 px-[4vmin] pb-[2.4vmin] text-[1.9vmin] text-muted">
      <div className="flex min-w-0 items-center gap-4">
        <span className="flex items-center gap-2">
          <span className={`inline-block size-[1.5vmin] rounded-full ${screen ? (screenOnline ? 'bg-go' : 'bg-bad') : dotClass[connection]}`} />
          {screen ? (screenOnline ? 'Kiosk screen online' : 'Kiosk screen offline') : statusLabel[connection]}
        </span>
        {/* Stale while the display itself is offline, so only shown when connected. */}
        {printer && connection === 'connected' && <PrinterStatus printer={printer} />}
        {kioskCode && (
          <span className="rounded-md bg-white/5 px-2 py-0.5 font-mono tracking-wider text-text/80 ring-1 ring-line">
            {kioskCode}
          </span>
        )}
      </div>
      <PoweredBy />
      <div className="justify-self-end tabular-nums">
        {dateFmt.format(now)} · <span className="font-semibold text-text">{timeFmt.format(now)}</span>
      </div>
    </footer>
  );
}

/** "Powered by ParthAI Pvt Ltd." with the mark: quiet, so the business's own name stays the one customers notice. */
function PoweredBy() {
  return (
    <div className="flex items-center gap-[1vmin] text-[1.6vmin] whitespace-nowrap text-muted/70">
      <img src="/brand/parthai-mark.png" alt="" className="size-[2.2vmin] object-contain" />
      <span>
        Powered by <span className="font-semibold text-muted">ParthAI Pvt Ltd.</span>
      </span>
    </div>
  );
}

function PrinterStatus({ printer }: { printer: Printer }) {
  const { label, dot } = printerStatus(printer);
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span className={`inline-block size-[1.5vmin] shrink-0 rounded-full ${dot}`} />
      <span className="max-w-[60vmin] truncate">{label}</span>
    </span>
  );
}
